/* eslint-disable lingui/no-unlocalized-strings */

import { WarningCircle } from "@phosphor-icons/react";
import { Button, Dialog, DialogContent } from "@reactive-resume/ui";

import { useCoachOptimizationStore } from "@/client/stores/coach-optimization";

import { useCoachUnloadGuard } from "./use-coach-unload-guard";

/**
 * One-time modal warning the student not to close or refresh this tab, paired with the
 * `beforeunload` guard that enforces it.
 *
 * WHY A MODAL AND NOT A NOTICE IN THE RAIL. This replaced an inline panel under `Save and Continue`.
 * The rail is long and scrolls, so the warning was routinely never seen — and the consequence it
 * describes is silent and unrecoverable-looking: close the tab and the résumé is saved but never
 * attached, so the job score simply does not move and nothing says why. That needs to be read once,
 * up front, rather than sit where it may be scrolled past.
 *
 * ONLY WHEN A SCORE IS COMING BACK. `useCoachUnloadGuard` requires both `window.opener` and
 * `session.returnOrigin`, because those are exactly what `Save and Continue` needs to hand the résumé
 * back. Without them there is no return trip to protect and the modal would be warning about a
 * consequence that has already happened — the confirm bar handles that case separately by pointing
 * the student at the résumé picker.
 *
 * ONCE PER RUN. The acknowledgement lives in the coach optimization store (sessionStorage), so it
 * survives the intake → builder navigation and a refresh of the builder, and resets on the next
 * optimization. Re-showing it on every remount would train the student to dismiss it unread.
 *
 * DISMISSING IT DOES NOT DISABLE THE GUARD. The `beforeunload` prompt stays registered for as long as
 * the handoff is pending; "Understood" acknowledges the warning, it does not waive it.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const CoachUnloadWarning = () => {
  const guarded = useCoachUnloadGuard();
  const acknowledged = useCoachOptimizationStore((state) => state.unloadNoticeAcknowledged);
  const acknowledge = useCoachOptimizationStore((state) => state.acknowledgeUnloadNotice);

  const open = guarded && !acknowledged;

  return (
    <Dialog open={open}>
      {/* The close "X" is hidden and outside-interaction is blocked, so the only way out is the
          button. A warning the student can dismiss by clicking the backdrop is a warning they can
          dismiss without reading, which is the failure mode of the notice this replaced. */}
      <DialogContent
        className="max-w-md [&>button]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="flex flex-col items-center gap-y-3 py-2 text-center">
          <WarningCircle size={44} weight="fill" className="text-warning" aria-hidden="true" />

          <h3 className="text-lg font-semibold">Keep this tab open</h3>

          <p className="text-sm leading-relaxed text-foreground/70">
            Do not close or refresh this tab, otherwise your score will not be updated for this job.
          </p>

          <Button size="lg" className="mt-2 w-full" onClick={acknowledge}>
            Understood
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
